import { Link } from 'react-router-dom';
import { cn, StatusBadge } from '@immo/ui-kit';
import type { LandIqBoardTicket } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { PlannedEstimate } from '@/components/landiq/run-estimate';
import { STATE_TONES } from '@/lib/landiq/entry-state-tones';

type Entry = LandIqBoardTicket['entries'][number];

/** Failed entries first, so the ones to restart lead; otherwise LandIQ's order (Stichtag by Stichtag). */
export function entriesFailedFirst(entries: ReadonlyArray<Entry>): ReadonlyArray<Entry> {
  return [...entries.filter((e) => e.state === 'FAILED'), ...entries.filter((e) => e.state !== 'FAILED')];
}

type Props = {
  readonly id?: string;
  readonly ticket: LandIqBoardTicket;
  readonly currentEntryId?: string;
  /** On a draggable card the links must not start a native link drag. */
  readonly draggable?: boolean;
  readonly className?: string;
};

/**
 * Every manifest entry of a ticket, each a link to its own run page, so any of them can be opened and
 * restarted — not only the one LandIQ picked as the ticket's open entry. Links sit above a card's stretched link.
 */
export function TicketEntryList({ id, ticket, currentEntryId, draggable, className }: Props) {
  const t = useTranslations('pages.admin.landiq-data-runs');

  return (
    <ul id={id} aria-label={t('entries.label', { ticket: ticket.ticket })} className={cn('flex flex-col', className)}>
      {entriesFailedFirst(ticket.entries).map((entry) => {
        const current = entry.id === currentEntryId;

        return (
          <li key={entry.id} className="flex min-w-0 items-center justify-between gap-2 py-1">
            <Link
              to={`/landiq/data-runs/${encodeURIComponent(entry.id)}`}
              draggable={draggable ? false : undefined}
              aria-current={current ? 'page' : undefined}
              className={cn(
                'relative z-10 min-w-0 break-all font-mono text-xs hover:underline',
                current ? 'font-semibold text-text-primary' : 'text-text-secondary',
              )}
            >
              {entry.id}
            </Link>
            <span className="flex shrink-0 items-center gap-1.5 text-xs text-text-tertiary">
              {entry.estimateMinutes !== null && (
                <PlannedEstimate
                  minutes={entry.estimateMinutes}
                  className={cn('tabular-nums', entry.lastRunEstimate?.overEstimate && 'font-medium text-warning-700')}
                />
              )}
              {t(`board.columns.${entry.stage}`)}
              <StatusBadge size="sm" tone={STATE_TONES[entry.state]} label={t(`states.${entry.state}`)} />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
